// Import required libraries and modules
import cryptoJs from "crypto-js"; // For encrypting/decrypting data
import securekey from "../../../config"; // Secret key used for AES encryption/decryption
import {useDispatch, useSelector} from "react-redux"; // Redux hooks for dispatching actions and accessing state
import {useEffect, useState} from "react"; // React hooks
import {
    CreateSupplierDocValue,
    GetAllSupplierDocumentsById,
    SetSupplierDocValue
} from "../../../reduxToolkit/Providers/providersApi"; // Supplier document-related actions
import {
    CreateContractorDocValue,
    GetAllContractorDocuments,
    SetContractorDocValue
} from "../../../reduxToolkit/Contractor/ContractorApi"; // Contractor document-related actions
import {GetUserDocumentsEmployee} from "../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi"; // Employee document fetch action
import {DeleteItemsApi} from "../../../reduxToolkit/Commons/CommonsApi"; // General API to delete documents
import {useTranslation} from "react-i18next"; // i18n translation hook
import {CreateEmployeeDocValue, SetEmployeeDocValue} from "../../../reduxToolkit/Employee/EmployeeApi"; // Employee document-related actions
import {USER_ACCESS_KEY_MODEL, USER_TYPE_DOCUMENT} from "../../../enums/userTypeEnum"; // Enum definitions

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Main component for managing user documents based on their type (supplier, contractor, or employee)
const UserDocumentsApp = ({docType}) => {
    const { t } = useTranslation(); // Get translation function
    const userdata = sessionStorage.getItem('userdata'); // Retrieve encrypted user data from session
    const bytess = cryptoJs.AES.decrypt(userdata || '', securekey); // Decrypt it using the secure key
    const userstring = bytess.toString(cryptoJs.enc.Utf8); // Convert decrypted bytes to string
    const user = userstring ? JSON.parse(userstring)?.data?.data : ''; // Parse JSON to extract user data

    const dispatch = useDispatch(); // Redux dispatcher

    // Define component state variables
    const [selectDocumentsForDelete, setSelectDocumentsForDelete] = useState([]); // List of selected documents for deletion
    const [isAllChecked, setIsAllChecked] = useState(false); // State for "select all" checkbox
    const [deleteDocShow, setDeleteDocShow] = useState(false); // Toggle deletion confirmation modal
    const [updated, setUpdated] = useState(0); // Trigger to refetch on update
    const [deletedFlag, setDeletedFlag] = useState(0); // Trigger to refetch on delete
    const { uploadImg, changeCount } = useSelector((state) => state.sharedSlice); // Shared state from Redux
    const [information, setInformation] = useState(''); // Instruction text based on user type
    const [optionDownload, setOptionDownload] = useState(''); // Enum used for API table reference
    const [docValue, setDocValue] = useState(null); // Set function for updating a document
    const [createDocValue, setCreateDocValue] = useState(null); // Function for creating a document
    const [dataTable, setDataTable] = useState([]); // Table data of user's documents

    // Set variables and functions based on document type when component mounts or docType changes
    useEffect(() => {
        switch (docType) {
            case USER_ACCESS_KEY_MODEL.SUPPLIER:
                setInformation(t('please_attach_document_supplier'));
                setOptionDownload(USER_TYPE_DOCUMENT.SUPPLIER_DOCUMENT);
                setDocValue(() => SetSupplierDocValue);
                setCreateDocValue(() => CreateSupplierDocValue);
                break;

            case USER_ACCESS_KEY_MODEL.CONTRACTOR:
                setInformation(t('please_attach_document_contractor'));
                setOptionDownload(USER_TYPE_DOCUMENT.CONTRACTOR_DOCUMENT);
                setDocValue(() => SetContractorDocValue);
                setCreateDocValue(() => CreateContractorDocValue);
                break;

            case USER_ACCESS_KEY_MODEL.EMPLOYEE:
                setInformation(t('please_attach_document_employee'));
                setOptionDownload(USER_TYPE_DOCUMENT.EMPLOYEE_DOCUMENT);
                setDocValue(() => SetEmployeeDocValue);
                setCreateDocValue(() => CreateEmployeeDocValue);
                break;

            default:
                break;
        }
    }, [docType, t]);

    // Fetch document data when upload, update, or delete state changes
    useEffect(() => {
        getData();
    }, [uploadImg, changeCount, updated, deletedFlag]);

    // Fetch documents based on user type
    const getData = () => {
        switch (docType) {
            case USER_ACCESS_KEY_MODEL.SUPPLIER:
                dispatch(GetAllSupplierDocumentsById(user?.id)).then(({payload:{data:{data}}}) => {
                    if(data?.length > 0 ) setDataTable(data);
                });
                break;
            case USER_ACCESS_KEY_MODEL.CONTRACTOR:
                dispatch(GetAllContractorDocuments(user?.id)).then(({payload:{data:{data}}}) => {
                    if(data?.length > 0 ) setDataTable(data);
                });
                break;
            case USER_ACCESS_KEY_MODEL.EMPLOYEE:
                dispatch(GetUserDocumentsEmployee(user?.id)).then(({payload:{data:{data}}}) => {
                    if(data?.length > 0 ) setDataTable(data);
                });
                break;
            default:
                return null;
        }
    };

    // Delete selected documents
    const deleteSelectedDocuments = (deleteItem) => {
        const body = deleteItem;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName:optionDownload, body })).then(
                ({
                     payload: {
                         data: { success },
                     },
                 }) => {
                    if (success === true) {
                        setDeletedFlag(deletedFlag + 1); // Trigger refresh
                        setSelectDocumentsForDelete([]); // Reset selection
                        setIsAllChecked(false); // Uncheck "select all"
                    }
                },
            );
        }
    };

    // Return required variables and methods for the component
    return {
        t, selectDocumentsForDelete, setSelectDocumentsForDelete, deleteDocShow, setDeleteDocShow,
        isAllChecked, setIsAllChecked, deleteSelectedDocuments, information, dataTable, optionDownload, docValue, createDocValue,
        updated, setUpdated
    };
};

export default UserDocumentsApp; // Export component
